import { Button, Checkbox, Divider, RadioGroup } from "@mui/material";
import MDAutocomplete from "components/MDAutocomplete";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDDatePicker from "components/MDDatePicker";
import MDEditor from "components/MDEditor";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import RichTextEditor from "components/common/RichTextEditor";
import { Field } from "formik";
import { DatePicker, TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const ViewTemplateForms = ({ templateData }) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {templateData?.map(
        (template, templateIndex) =>
          template?.service_add_form_fields?.length && (
            <>
              <MDTypography key={template.id} variant="h6">
                {template?.name}
              </MDTypography>

              <MDBox
                sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}
              >
                <MDBox
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  p={3}
                  rowGap={3}
                  columnGap={3}
                  width="70rem"
                >
                  {template?.service_add_form_fields?.map((formField) => (
                    <MDBox key={formField.id}>
                      {/* Render text input field */}
                      {formField.type === "text" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDInput
                              {...field}
                              label={formField.label}
                              required={formField.required}
                              fullWidth
                              inputProps={{
                                minLength: formField.minlength,
                                maxLength: formField.maxlength,
                              }}
                              error={meta.touched && meta.error}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render textarea field */}
                      {formField.type === "textarea" && (
                        <>
                          <MDTypography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">
                            {formField.label}
                          </MDTypography>
                          <Field name={formField.name}>
                            {({ field, meta }) => (
                              <RichTextEditor
                                {...field}
                                value={field.value}
                                onChange={(content) => setFieldValue(field.name, content)}
                                onBlur={() => setFieldValue(field.name, field.value)}
                                error={meta.touched && meta.error}
                                helperText={meta.touched && meta.error}
                              />
                            )}
                          </Field>
                        </>
                      )}
                      {/* Render number input field */}
                      {formField.type === "number" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDInput
                              {...field}
                              type="number"
                              fullWidth
                              label={formField.label}
                              required={formField.required}
                              error={meta.touched && meta.error}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render select/autocomplete field */}
                      {formField.type === "select" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <MDAutocomplete
                              {...field}
                              label={formField.label}
                              options={formField?.default_field?.options ?? []}
                              multiple={formField.multiple === true}
                              required={formField.required}
                              fullWidth
                              onChange={(event, value) => setFieldValue(field.name, value)}
                              error={meta.touched && meta.error}
                              renderInput={(params) => (
                                <MDInput {...params} label={formField.label} />
                              )}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render checkbox field */}
                      {formField.type === "checkbox" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <Checkbox
                              {...field}
                              row
                              label={formField.label}
                              options={[
                                {
                                  label: "Option 1",
                                  value: "option1",
                                },
                                {
                                  label: "Option 2",
                                  value: "option2",
                                },
                              ]}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                              }}
                              error={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}
                      {/* Render radio group field */}
                      {formField.type === "radio-group" && (
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <RadioGroup
                              {...field}
                              row
                              label={
                                formField.label.includes("&amp;")
                                  ? formField.label.replace("&amp;", "&")
                                  : formField.label
                              }
                              options={[
                                {
                                  label: "Option 1",
                                  value: "option1",
                                },
                                {
                                  label: "Option 2",
                                  value: "option2",
                                },
                              ]}
                              error={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      )}

                      {/* Render date picker field using MUI DatePicker */}
                      {formField.type === "date" && formField.subtype === "date" && (
                        // DatePicker for date selection
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <DatePicker
                              label={formField.label}
                              value={field.value || null}
                              onChange={(value) => setFieldValue(field.name, value)}
                              slotProps={{
                                textField: {
                                  InputLabelProps: {
                                    style: {
                                      paddingRight: "1.4rem",
                                    },
                                  },
                                  fullWidth: true,
                                  required: formField.required,
                                  error: meta.touched && Boolean(meta.error),
                                  helperText: meta.touched && meta.error,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}

                      {/* Render time picker field using MUI TimePicker */}
                      {formField.type === "date" && formField.subtype === "time" && (
                        // TimePicker for time selection
                        <Field name={formField.name}>
                          {({ field, meta }) => (
                            <TimePicker
                              label={formField.label}
                              value={field.value || null}
                              onChange={(value) => setFieldValue(field.name, value)}
                              slotProps={{
                                textField: {
                                  InputLabelProps: {
                                    style: {
                                      paddingRight: "1.4rem",
                                    },
                                  },
                                  fullWidth: true,
                                  required: formField.required,
                                  error: meta.touched && Boolean(meta.error),
                                  helperText: meta.touched && meta.error,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}
                    </MDBox>
                  ))}
                </MDBox>
                {/* Delete button for template */}

                <MDButton variant="outlined" color="error" size="small">
                  Delete
                </MDButton>
              </MDBox>
              {/* Add more button */}
              <MDButton variant="contained" color="primary" size="small" sx={{ mt: 2, mb: 2 }}>
                Add More
              </MDButton>
              {/* Divider between templates */}
              {templateIndex !== templateData.length - 1 && <Divider sx={{ mb: 2, mt: 1 }} />}
            </>
          )
      )}
    </LocalizationProvider>
  );
};

export default ViewTemplateForms;
